import { useState, useEffect } from 'react';
import '../../../scss/workoffice.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { GetUserFullInformation } from '../../../reduxToolkit/Commons/CommonsApi';
import { WORK_STATUS, WORK_STATUS_ACTIONS } from '../Model/model';
import moment from 'moment';
import { errorMsg } from '../../../Apis/functions';
import { accessMethod, accessMethodModel } from '../../../enums/accessMethodEnum';
import { CheckOutWorkOffice, GetWorkOffice, CheckInWorkOffice } from './../../../reduxToolkit/Logs/LogApi';

/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */

export const WorkOfficeApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [checkIn, setCheckIn] = useState(null);
    const [checkOut, setCheckOut] = useState(null);
    const { getWorkOffice } = useSelector((state) => state.LogSlice);
    const { idSet } = useSelector((state) => state.CommonsSlice);
    const { getUserFullInformation } = useSelector((state) => state.CommonsSlice);
    const [showSpinner, setShowSpinner] = useState(false);
    const [status, setStatus] = useState(WORK_STATUS.READY);
    const [location, setLocation] = useState(null);
    const [modified, setModified] = useState(false);

    const ensureLocation = () =>
        new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const coords = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    };

                    setLocation(coords);
                    resolve(coords);
                },
                reject,
                {
                    enableHighAccuracy: false,
                    maximumAge: 300000,
                },
            );
        });

    useEffect(() => {
        if (!navigator.geolocation) {
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            () => {},
            {
                enableHighAccuracy: false,
                maximumAge: 300000,
            },
        );
    }, []);

    // Function to handle check in action
    const checkInFn = async () => {
        try {
            let currentLocation = location;

            if (!currentLocation) {
                currentLocation = await ensureLocation();
            }

            setShowSpinner(true);
            const data = {
                accessMethodIdIn: accessMethodModel.WEB_APP.id,
                checkInLatitude: currentLocation.latitude,
                checkInLongitude: currentLocation.longitude,
            };
            dispatch(CheckInWorkOffice(data)).then(
                ({
                    payload: {
                        data: { data, success, code },
                    },
                }) => {
                    setShowSpinner(false);
                    if (success === true) {
                        setCheckIn(data?.data?.checkIn);
                        dispatch(GetWorkOffice());
                        toast.success(t('has_been_updated_successfully'));
                    } else {
                        errorMsg(code);
                    }
                },
            );
        } catch (error) {
            setShowSpinner(false);
            toast.info(t('we_need_the_access_to_check_your_check_in'));
        }
    };

    // Function to handle checkout action
    const checkOutFn = async () => {
        try {
            let currentLocation = location;

            if (!currentLocation) {
                currentLocation = await ensureLocation();
            }
            setShowSpinner(true);
            const data = {
                accessMethodIdOut: accessMethodModel.WEB_APP.id,
                checkOutLatitude: currentLocation.latitude,
                checkOutLongitude: currentLocation.longitude,
            };
            dispatch(CheckOutWorkOffice(data)).then(
                ({
                    payload: {
                        data: { data, success, code },
                    },
                }) => {
                    setShowSpinner(false);
                    if (success === true) {
                        setCheckOut(data?.data?.checkOut);
                        dispatch(GetWorkOffice());
                        toast.success(t('has_been_updated_successfully'));
                    } else {
                        errorMsg(code);
                    }
                },
            );
        } catch (error) {
            setShowSpinner(false);
            toast.info(t('we_need_the_access_to_check_your_checkout'));
        }
    };

    // useEffect to fetch the user photo based on the user ID
    useEffect(() => {
        const fetchGetPhotoFromUser = async () => {
            await dispatch(GetUserFullInformation(idSet));
        };
        if (idSet) {
            fetchGetPhotoFromUser();
        }
    }, [idSet]);

    /**
     * Update work office status
     *
     * READY:
     * User has not checked in yet.
     *
     * WORKING:
     * User already checked in but not checked out.
     *
     * COMPLETE:
     * User completed check out.
     */
    useEffect(() => {
        if (!getWorkOffice?.checkIn && !getWorkOffice?.checkOut) {
            setStatus(WORK_STATUS.READY);
        }

        if (getWorkOffice?.checkIn && !getWorkOffice?.checkOut) {
            setStatus(WORK_STATUS.WORKING);
        }

        if (getWorkOffice?.checkIn && getWorkOffice?.checkOut) {
            setStatus(WORK_STATUS.COMPLETE);
        }
    }, [getWorkOffice]);

    /**
     * Calculate total worked minutes between check-in and check-out
     */
    const totalHours =
        getWorkOffice?.checkIn && getWorkOffice?.checkOut
            ? moment(getWorkOffice?.checkOut).diff(moment(getWorkOffice?.checkIn), 'minute')
            : 0;

    /**
     * Convert total minutes into hours
     */
    const hours = Math.floor(totalHours / 60);

    /**
     * Get remaining minutes after calculating hours
     */
    const minutes = totalHours % 60;

    /**
     * Action handlers based on current work status
     *
     * READY:
     * Executes check-in action
     *
     * WORKING:
     * Executes check-out action
     *
     * COMPLETE:
     * Allows updating check-out action
     */
    const actionHandlers = {
        [WORK_STATUS.READY]: checkInFn,
        [WORK_STATUS.WORKING]: checkOutFn,
        [WORK_STATUS.COMPLETE]: checkOutFn,
    };

    /**
     * Get current button/action configuration
     * based on active work status
     */
    const currentAction = WORK_STATUS_ACTIONS[status];
    useEffect(() => {
        dispatch(GetWorkOffice()).then(({ payload: { data } }) => {});
    }, []);
    return {
        checkIn,
        checkOut,
        checkInFn,
        checkOutFn,
        getWorkOffice,
        getUserFullInformation,
        t,
        status,
        totalHours,
        hours,
        minutes,
        currentAction,
        actionHandlers,
        showSpinner,
    };
};
